<template>
  <div :class="confirmation && 'lib__visible-print'" class="lib__form__main" data-cy="resume-component">
    <ValidationObserver ref="validator" slim>
      <div class="lib__form__main__content">
        <header class="lib__form__main__header">
          <h2 class="lib__h2">{{ $t('resume.smartMeter.title') }}</h2>
        </header>

        <resume-section-title minimumBadgeWidth :read-only="readOnly" :step="1" @goToStep="goToStep($event)">
          <template v-slot:icon>1</template>
          <template>{{ $t('resume.identification') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <ul class="lib__infobox-list">
              <InfoBox item level="success">
                <template v-slot:icon>
                  <div class="lib__tooltips lib__tooltips--top-left">
                    <span>
                      <i class="fas fa-map-marker-alt"></i>
                    </span>
                    <span class="lib__tooltips__data">{{ $t('identification.smartMeter.addressTooltip') }}</span>
                  </div>
                </template>
                <template>
                  <h3 class="lib__text-uppercase">{{ $t('identification.smartMeter.address') }}</h3>
                  <p>
                    {{ checkEanResultState.consumptionAddress.houseNumber }}
                    {{ checkEanResultState.consumptionAddress.streetName }}
                    {{ checkEanResultState.consumptionAddress.complement || '' }}
                    {{ checkEanResultState.consumptionAddress.complementBox || '' }} -
                    {{ checkEanResultState.consumptionAddress.zipCode }}
                    {{ checkEanResultState.consumptionAddress.cityName }}
                  </p>
                </template>
              </InfoBox>
              <InfoBox item level="primary">
                <template v-slot:icon>
                  <img :src="require('@ores/css-library/assets/img/icons/electricity.svg')" alt="electricity" />
                </template>
                <template>
                  <h3 class="lib__text-uppercase">{{ $t('identification.smartMeter.eanCode') }}</h3>
                  <p>{{ eanCodeState }}</p>
                </template>
              </InfoBox>
            </ul>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="2" @goToStep="goToStep($event)">
          <template v-slot:icon>2</template>
          <template>{{ $t('resume.request') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <div class="lib__infobox-list">
              <resume-info-item :state="energyProductionInfoState.hasEnergyProduction === -1 ? null : !!energyProductionInfoState.hasEnergyProduction" :title="$t('resume.energyProduction')" icon="fa-bolt"></resume-info-item>
            </div>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="true" :step="-1">
          <template v-slot:icon>3</template>
          <template>{{ $t('meter.offerPrice') }}</template>
        </resume-section-title>
        <resume-container noMarginTop commonMobileAndDesktopLayout>
          <template v-slot:common>
            <SmartMeterPriceCalculator :isIndividual="contactInfoState.contactType === 'individual'">
            </SmartMeterPriceCalculator>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="3" @goToStep="goToStep($event)">
          <template v-slot:icon>4</template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info :address="checkEanResultState.consumptionAddress" :contact-info="contactInfoState"></resume-contact-info>
          </template>
        </resume-container>

        <resume-section-title minimumBadgeWidth noMarginTop :read-only="readOnly" :step="4" @goToStep="goToStep($event)">
          <template v-slot:icon>5</template>
          <template>{{ $t('resume.vatInfo') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-vat-info :company-vat-info-selection="companyVatInfoSelectionState" :contact-info="contactInfoState" :freelance-vat-info-selection="freelanceVatInfoSelectionState" :individual-vat-info-selection="individualVatInfoSelectionState"></resume-vat-info>
          </template>
        </resume-container>

      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator'
import { State } from 'vuex-class'

import Step from '@/components/steps/Step'
import PriceAnnexe from '@/components/display/PriceAnnexe.vue'
import SmartMeterPriceCalculator from '@/components/display/SmartMeterPriceCalculator.vue'
import GasPriceCalculator from '@/components/display/GasPriceCalculator.vue'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import ResumeVatInfo from '@/components/display/Resume/ResumeVatInfo.vue'
import ResumeAddress from '@/components/display/Resume/ResumeAddress.vue'
import {
  EnergyProductionInfoModel,
  ContactInfo,
  IndividualVatInfoSelection,
  FreelanceVatInfoSelection,
  CompanyVatInfoSelection,
  CheckEanSmartMeterResult
} from '@/models'
import { dateService, calendarService, timeService } from '@ores/vue-library'

@Component({
  name: 'summary-step',
  components: {
    PriceAnnexe,
    SmartMeterPriceCalculator,
    GasPriceCalculator,
    ResumeContactInfo,
    ResumeVatInfo,
    ResumeAddress,
  }
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly confirmation!: boolean
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  public dateService = dateService
  public calendarService = calendarService
  public timeService = timeService

  @State('noAppointment', { namespace: 'smartMeterConnection' })
  public noAppointment!: boolean

  @State('eanCode', { namespace: 'smartMeterConnection/identification' })
  public eanCodeState!: string

  @State('checkEanResult', { namespace: 'smartMeterConnection/identification' })
  public checkEanResultState!: CheckEanSmartMeterResult

  @State('energyProductionInfo', { namespace: 'smartMeterConnection/energyProduction' })
  public energyProductionInfoState!: EnergyProductionInfoModel

  @State('contactInfo', { namespace: 'smartMeterConnection/contactInfo' })
  public contactInfoState!: ContactInfo

  @State('individualVatInfoSelection', { namespace: 'smartMeterConnection/vatInfo' })
  public individualVatInfoSelectionState!: IndividualVatInfoSelection | null
  @State('freelanceVatInfoSelection', { namespace: 'smartMeterConnection/vatInfo' })
  public freelanceVatInfoSelectionState!: FreelanceVatInfoSelection | null
  @State('companyVatInfoSelection', { namespace: 'smartMeterConnection/vatInfo' })
  public companyVatInfoSelectionState!: CompanyVatInfoSelection | null

  @State('fileNumber', { namespace: 'smartMeterConnection/stakeholder' })
  public fileNumber!: string | null

  @State('isProsumer', { namespace: 'smartMeterConnection/stakeholder' })
  public isProsumer!: boolean

  @State('requestId', { namespace: 'smartMeterConnection' })
  public requestId!: string
  //#endregion


  //#region [Methods]
  public async save(next: boolean, valid: boolean) {
    this.$appInsights.trackEvent('WR_SMC_StepSummary_Validated', {
      Segment: 'WR_SMC',
      WebRef: this.requestId
    })
  }

  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  //#endregion
}
</script>